import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { BaseUrl } from "../Routes/BaseUrl";
import { toast } from "react-toastify";
import { Col, Row, Form, Button, Card } from "@themesberg/react-bootstrap";

const InputField = ({ label, name, control, rules, type = "text", disabled = false }) => (
  <Form.Group className="mb-3">
    <Form.Label>{label}</Form.Label>
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <>
          <Form.Control {...field} type={type} isInvalid={!!error} disabled={disabled} />
          <Form.Control.Feedback type="invalid">{error?.message}</Form.Control.Feedback>
        </>
      )}
    />
  </Form.Group>
);

const SelectField = ({ label, name, control, rules, options }) => (
  <Form.Group className="mb-3">
    <Form.Label>{label}</Form.Label>
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <>
          <Form.Select {...field} isInvalid={!!error}>
            <option value="">Select {label}</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">{error?.message}</Form.Control.Feedback>
        </>
      )}
    />
  </Form.Group>
);

export const ProductEditComponent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(null);
  
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  const categories = ['Skin care', 'Hair Care', 'Baby and Mom care', 'Sexual Wellness', 'Oral Care'];

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${BaseUrl.trim()}/admin/products/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const product = response.data.data;
      reset({
        name: product.name,
        description: product.description,
        price: product.price,
        category: product.category,
        subCategory: product.subCategory,
        brand: product.brand,
        stock: product.stock,
        unit: product.unit,
      });
      
      setCurrentImage(product.image);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load product");
      console.error(error);
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      const formDataToSend = new FormData();
      Object.keys(data).forEach((key) => {
        if (data[key] !== undefined && data[key] !== null) {
          formDataToSend.append(key, data[key]);
        }
      });

      if (data.image?.length > 0) {
        formDataToSend.append("image", data.image[0]);
      }

      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${BaseUrl.trim()}/admin/products/${id}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Product updated successfully");
        navigate("/ProductList");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };

  if (loading) {
    return <div className="text-center py-5">Loading product...</div>;
  }

  return (
    <Card className="border-0 shadow">
      <Card.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <h5 className="mb-4">Edit Product</h5>
          
          <Row>
            <Col md={6}>
              <InputField
                label="Product Name"
                name="name"
                control={control}
                rules={{ required: "Product name is required" }}
              />
            </Col>
            <Col md={6}>
              <InputField
                label="Price (Rs)"
                name="price"
                control={control}
                type="number"
                rules={{ required: "Price is required", min: { value: 0, message: "Price must be positive" } }}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <SelectField
                label="Category"
                name="category"
                control={control}
                rules={{ required: "Category is required" }}
                options={categories}
              />
            </Col>
            <Col md={6}>
              <InputField
                label="Sub Category"
                name="subCategory"
                control={control}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <InputField
                label="Brand"
                name="brand"
                control={control}
              />
            </Col>
            <Col md={6}>
              <InputField
                label="Stock"
                name="stock"
                control={control}
                type="number"
                rules={{ min: { value: 0, message: "Stock must be positive" } }}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <InputField
                label="Unit (e.g., piece, ml, gm)"
                name="unit"
                control={control}
              />
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Product Image</Form.Label>
                {currentImage && (
                  <div className="mb-2">
                    <img src={currentImage} alt="Current" style={{ maxWidth: "200px", maxHeight: "150px" }} />
                    <p className="text-muted small">Current image (upload new to replace)</p>
                  </div>
                )}
                <Controller
                  name="image"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Form.Control
                      {...field}
                      type="file"
                      accept="image/*"
                      onChange={(e) => onChange(e.target.files)}
                    />
                  )}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <Form.Control as="textarea" rows={4} {...field} />
                  )}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex gap-2">
            <Button type="submit" variant="success">
              Update Product
            </Button>
            <Button variant="secondary" onClick={() => navigate("/ProductList")}>
              Cancel
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};
